import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, ListGroup, ListGroupItem } from 'reactstrap';

import './RedundantOverridesAlert.css';

/**
 * Warning banner listing overrides identical to the default, with per-item and bulk remove.
 * Intended to sit inline just above the add-override control.
 *
 * Uses `alert-inline` so #root .alert { position: fixed } (toast) does not apply.
 *
 * @param {{
 *   items: { id: string, label: string, icon?: string }[],
 *   onRemove: (id: string) => void,
 *   onRemoveAll?: () => void,
 *   className?: string,
 * }} props
 */
const RedundantOverridesAlert = ({ items, onRemove, onRemoveAll, className }) => {
    const { t } = useTranslation();

    if (!Array.isArray(items) || items.length === 0)
        return null;

    const remove_all = () => {
        if (onRemoveAll) {
            onRemoveAll();
            return;
        }
        items.forEach(item => onRemove(item.id));
    };

    const alert_class = ['RedundantOverridesAlert', 'alert', 'alert-warning', 'alert-inline', 'mb-2', className]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={alert_class} role="alert">
            <div className="alert-inline-body w-100">
                <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                    <strong className="RedundantOverridesAlert-title">
                        {t('tickettypes.redundant_overrides.title')}
                    </strong>
                    <Button
                        type="button"
                        color="danger"
                        size="sm"
                        className="RedundantOverridesAlert-action"
                        onClick={remove_all}
                    >
                        <i className="fa fa-trash" aria-hidden="true" />
                        &nbsp;{t('tickettypes.redundant_overrides.remove_all')}
                    </Button>
                </div>
                <p className="mb-2 small">
                    {t('tickettypes.redundant_overrides.help')}
                </p>
                <ListGroup flush className="RedundantOverridesAlert-list">
                    {items.map(item => (
                        <ListGroupItem
                            key={item.id}
                            className="d-flex justify-content-between align-items-center px-0 bg-transparent border-0"
                        >
                            <Badge color="dark" pill className="RedundantOverridesAlert-itemBadge">
                                {item.icon && (
                                    <i className={`fa ${item.icon} mr-1`} aria-hidden="true" />
                                )}
                                {item.label}
                            </Badge>
                            <Button
                                type="button"
                                color="danger"
                                size="sm"
                                className="RedundantOverridesAlert-action"
                                onClick={() => onRemove(item.id)}
                            >
                                <i className="fa fa-trash" aria-hidden="true" />
                                &nbsp;{t('tickettypes.redundant_overrides.remove_one')}
                            </Button>
                        </ListGroupItem>
                    ))}
                </ListGroup>
            </div>
        </div>
    );
};

export default RedundantOverridesAlert;
